import { Badge } from "@/components/ui/badge";
import { fmtBid, tierColor, tierLetter } from "./leadUtils";
import type { DealLead } from "./types";

const TierBadge = ({ tier }: { tier: string | null }) => {
  const letter = tierLetter(tier);
  if (!letter) return null;
  return (
    <Badge
      className="text-white border-transparent"
      style={{ backgroundColor: tierColor(tier) }}
    >
      Tier {letter}
    </Badge>
  );
};

const specLine = (item: DealLead): string =>
  [
    item.beds ? `${item.beds} bd` : null,
    item.baths ? `${item.baths} ba` : null,
    item.sqft ? `${Math.round(item.sqft).toLocaleString()} sqft` : null,
    item.yearBuilt ? `Built ${item.yearBuilt}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

/**
 * InfoWindow body for a clicked marker. Rendered inside the Google Maps
 * InfoWindow, which has a white surface in light mode and a dark one when the
 * map's colorScheme is DARK — theme tokens don't reach in there, so colors
 * are inherited from the InfoWindow itself.
 */
export const LeadInfoContent = ({ item }: { item: DealLead }) => {
  const isAuction = item.kind === "auction";
  const price = isAuction
    ? (item.minimumBid ?? item.openingBid)
    : item.estValue;
  const specs = specLine(item);
  return (
    <div className="min-w-[230px] max-w-[300px] space-y-1 text-[13px] leading-snug">
      <div className="flex items-center gap-2">
        <span className="font-semibold">
          {isAuction ? "🏛️ Auction" : "⚖️ Probate"}
        </span>
        <TierBadge tier={item.dealTier} />
      </div>
      <div className="text-base font-bold">
        {price != null ? fmtBid(price) : isAuction ? "Bid TBD" : "—"}
      </div>
      <div className="font-medium">{item.address}</div>
      <div className="opacity-75">
        {isAuction
          ? [item.auctionType, item.county, item.saleDate]
              .filter(Boolean)
              .join(" · ")
          : [
              item.probateType,
              item.county,
              item.filingDate ? `filed ${item.filingDate}` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
      </div>
      {specs && <div className="opacity-75">{specs}</div>}
      {isAuction && item.equityPct != null && (
        <div className="opacity-90">
          Equity:{" "}
          {Math.round(
            item.equityPct > 1 ? item.equityPct : item.equityPct * 100,
          )}
          %
        </div>
      )}
      {isAuction && item.netProfit != null && (
        <div className="opacity-90">
          Est. net profit: {fmtBid(item.netProfit)}
        </div>
      )}
      {isAuction && item.owner && (
        <div className="opacity-75">Owner: {item.owner}</div>
      )}
      {!isAuction && item.decedent && (
        <div className="opacity-75">Decedent: {item.decedent}</div>
      )}
      {!isAuction && item.attorney && (
        <div className="opacity-75">Attorney: {item.attorney}</div>
      )}
    </div>
  );
};
